import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { isInsider } from "@/lib/levels";
import { UNLOCKS_EVENT } from "@/lib/unlocks";

/**
 * Wave C · "become an insider" bottom sheet (Vania 7 Oct 2026 · L5 mockup).
 *
 * Mounted once in MemberShell. Opens via window CustomEvent "ww:unlocks:open".
 * ONE sheet, every gate. Short-circuits for insiders.
 *
 *   title : "see what insiders posted and reviewed"
 *   body  : "insiders unlock the whole of watu wako:" followed by the four
 *           benefit lines.
 *   box   : founding insider · KES 200 a month, for life / N of 1,000 seats
 *           left · start free for 30 days.
 *   CTAs  : "become an insider" (kili fill) + "not now" (text link).
 *
 * Works both themes via tokens. Scrim darkens the surface behind.
 */
const META = "var(--ww-text-muted)";
const LINE = "var(--ww-line)";
const CARD = "var(--ww-surface)";
const YELLOW = "#FFD60A";
const API = process.env.REACT_APP_BACKEND_URL;

const BENEFITS = [
  "reviews and photos from the event",
  "follow curators and watu",
  "squads to plan with your people",
  "i went, to post your own",
];

export default function InsiderUnlocksSheet() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [remaining, setRemaining] = useState(null);

  const onOpen = useCallback((e) => {
    if (isInsider(user)) return;
    setReason((e?.detail?.reason) || "");
    setOpen(true);
  }, [user]);

  useEffect(() => {
    window.addEventListener(UNLOCKS_EVENT, onOpen);
    return () => window.removeEventListener(UNLOCKS_EVENT, onOpen);
  }, [onOpen]);

  useEffect(() => {
    if (!open) return;
    // Live seat count from /membership/status. Fire-and-forget.
    fetch(`${API}/api/membership/status`).then((r) => r.json())
      .then((d) => setRemaining(typeof d?.founding_remaining === "number" ? d.founding_remaining : null))
      .catch(() => {});
    const onKey = (ev) => { if (ev.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const signedIn = !!user;
  const primaryLabel = signedIn ? "become an insider" : "get in";
  const primaryGo = () => {
    setOpen(false);
    nav(signedIn ? "/app/membership" : "/app/signin?next=/app/membership");
  };
  const seatsLine = remaining == null
    ? "start free for 30 days"
    : `${remaining.toLocaleString()} of 1,000 seats left · start free for 30 days`;

  return (
    <div data-testid="insider-unlocks-sheet"
         onClick={() => setOpen(false)}
         style={{ position: "fixed", inset: 0, zIndex: 60,
                  background: "rgba(10,10,20,0.55)",
                  display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
           style={{ width: "100%", maxWidth: 520,
                    background: "var(--ww-ground)", color: "var(--ww-text)",
                    borderTopLeftRadius: 24, borderTopRightRadius: 24,
                    borderTop: `1px solid ${LINE}`,
                    padding: "20px 20px 28px", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <div>
            <p style={{ fontFamily: "Arial", textTransform: "uppercase",
                        letterSpacing: "0.22em", fontSize: 11, color: META }}>
              insiders
            </p>
            <h2 className="mt-1 lowercase" data-testid="unlocks-title"
                style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 22, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
              see what insiders posted and reviewed
            </h2>
          </div>
          <button aria-label="close" data-testid="unlocks-close"
                  onClick={() => setOpen(false)}
                  style={{ background: "transparent", border: 0, cursor: "pointer",
                           color: "var(--ww-text)", padding: 6, minHeight: 44, minWidth: 44,
                           display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <X size={20} />
          </button>
        </div>
        {reason && (
          <p className="mt-1" data-testid="unlocks-reason"
             style={{ fontFamily: "Arial", fontSize: 13, color: META }}>
            {reason}
          </p>
        )}

        <p className="mt-4" style={{ fontFamily: "Arial", fontSize: 14, color: META, lineHeight: 1.5 }}>
          insiders unlock the whole of watu wako:
        </p>
        <ul className="mt-2" data-testid="unlocks-list"
            style={{ fontFamily: "Arial", fontSize: 15, lineHeight: 1.7, color: "var(--ww-text)" }}>
          {BENEFITS.map((line) => (
            <li key={line} style={{ display: "flex", gap: 8, padding: "4px 0" }}>
              <span aria-hidden style={{ color: META }}>·</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 rounded-2xl p-4" data-testid="unlocks-founding-box"
             style={{ background: CARD, border: `1px solid ${LINE}` }}>
          <p style={{ fontFamily: "Arial", textTransform: "uppercase",
                      letterSpacing: "0.18em", fontSize: 11, color: META }}>
            founding insider
          </p>
          <p className="mt-1" style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 18 }}>
            <span className="ww-currency">KES</span> 200 a month, for life
          </p>
          <p className="mt-1" style={{ fontFamily: "Arial", fontSize: 13, color: META }}
             data-testid="unlocks-seats">
            {seatsLine}
          </p>
        </div>

        <button data-testid="unlocks-cta" onClick={primaryGo}
                style={{ width: "100%", marginTop: 20, padding: "16px 20px", borderRadius: 14,
                         background: YELLOW, color: "#1A1A30", border: `1px solid ${YELLOW}`,
                         fontFamily: "Poppins", fontWeight: 800, fontSize: 16,
                         textTransform: "lowercase", cursor: "pointer", minHeight: 56 }}>
          {primaryLabel}
        </button>
        <button data-testid="unlocks-not-now" onClick={() => setOpen(false)}
                style={{ width: "100%", marginTop: 10, padding: 10, background: "transparent",
                         color: META, border: 0, cursor: "pointer",
                         fontFamily: "Arial", fontSize: 14, textTransform: "lowercase",
                         textDecoration: "underline" }}>
          not now
        </button>
      </div>
    </div>
  );
}
